import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, User, Building2 } from 'lucide-react'
import { CoworkLogo } from '@/components/ui/CoworkLogo'
import { AuthBrandPanel } from '@/components/auth/AuthBrandPanel'
import { CheckEmailCard } from '@/components/auth/EmailVerificationCards'
import { FacebookIcon } from '@/components/ui/SocialIcons'
import { cn } from '@/lib/utils'
import { register, resendVerification, type CustomerType } from '@/services/authService'

const inputClass =
  'mt-1.5 flex h-12 w-full rounded-lg border border-[#E7E5FF] bg-white px-4 text-sm text-text shadow-sm transition-colors placeholder:text-text-muted/70 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/15'

const labelClass = 'text-sm font-medium text-text'

export default function RegisterPage() {
  const navigate = useNavigate()
  const [customerType, setCustomerType] = useState<CustomerType>('INDIVIDUAL')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  // สมัครสำเร็จและต้องยืนยันอีเมล — แสดงการ์ด "ยืนยันอีเมล" แทนฟอร์ม (CheckEmailCard จัดการปุ่มส่งซ้ำ/นับถอยหลังเอง)
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน')
      return
    }

    setLoading(true)
    try {
      // register ไม่ auto-login (ดู Flow 0 ใน knowledge-jos-cowork-api.md) — สมัครเสร็จพาไปหน้า login ต่อ
      // (ถ้าระบบบังคับยืนยันอีเมล จะแสดงหน้า "ตรวจอีเมลของคุณ" ก่อน)
      const result = await register({
        email,
        password,
        customer_type: customerType,
        ...(customerType === 'INDIVIDUAL'
          ? { first_name: firstName, last_name: lastName }
          : { company_name: companyName }),
      })
      if (result.requires_verification) {
        setPendingEmail(result.email)
        return
      }
      navigate('/login')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'สมัครสมาชิกไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen">
      <AuthBrandPanel />

      {/* Right — register form (45%) */}
      <div className="flex w-full flex-col items-center justify-center bg-white px-4 py-10 lg:w-[45%]">
        {/* Compact logo shown only when the branding panel is hidden */}
        <div className="mb-8 lg:hidden">
          <CoworkLogo />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-full max-w-[440px]"
        >
          {pendingEmail ? (
            <CheckEmailCard email={pendingEmail} onResend={() => resendVerification(pendingEmail)} />
          ) : (
          <>
          <div className="text-left">
            <h2 className="text-2xl font-semibold text-[#19172A]">สมัครสมาชิก</h2>
            <p className="mt-2 text-sm text-text-muted">สร้างบัญชีเพื่อเริ่มต้นใช้งาน JosCoWork</p>
          </div>

          <div className="mt-6 space-y-2">
            <label className={labelClass}>สมัครสมาชิกในฐานะ</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCustomerType('INDIVIDUAL')}
                className={cn(
                  'flex h-11 items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-colors',
                  customerType === 'INDIVIDUAL'
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-[#E7E5FF] text-text-muted hover:bg-[#F7F6FF]',
                )}
              >
                <User size={16} />
                บุคคลทั่วไป
              </button>
              <button
                type="button"
                onClick={() => setCustomerType('COMPANY')}
                className={cn(
                  'flex h-11 items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-colors',
                  customerType === 'COMPANY'
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-[#E7E5FF] text-text-muted hover:bg-[#F7F6FF]',
                )}
              >
                <Building2 size={16} />
                บริษัท / องค์กร
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-5">
            {customerType === 'INDIVIDUAL' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className={labelClass}>ชื่อ</label>
                  <input id="firstName" className={inputClass} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                </div>
                <div>
                  <label htmlFor="lastName" className={labelClass}>นามสกุล</label>
                  <input id="lastName" className={inputClass} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="companyName" className={labelClass}>ชื่อบริษัท</label>
                <input id="companyName" className={inputClass} value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
              </div>
            )}

            <div>
              <label htmlFor="email" className={labelClass}>อีเมล</label>
              <input id="email" type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className={labelClass}>รหัสผ่าน</label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className={cn(inputClass, 'pr-11')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                    aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                  >
                    {showPassword ? <EyeOff className="size-4.5" /> : <Eye className="size-4.5" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="confirmPassword" className={labelClass}>ยืนยันรหัสผ่าน</label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={cn(inputClass, 'pr-11')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                    aria-label={showConfirmPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                  >
                    {showConfirmPassword ? <EyeOff className="size-4.5" /> : <Eye className="size-4.5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border border-accent accent-accent"
              />
              <label htmlFor="terms" className="text-sm text-text-muted">
                ยอมรับ
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                  {' '}ข้อตกลงและเงื่อนไขการใช้บริการ
                </a>
              </label>
            </div>

            {error && <p className="text-sm text-rose-500">{error}</p>}

            <button
              type="submit"
              disabled={!agreed || loading}
              className="flex h-12 w-full items-center justify-center rounded-lg bg-gradient-to-r from-accent to-accent2 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:shadow-accent/40 disabled:pointer-events-none disabled:opacity-50"
            >
              {loading ? 'กำลังลงทะเบียน...' : 'ลงทะเบียน'}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="h-px w-full bg-[#E7E5FF]" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-text-muted">หรือ</span>
            </div>
          </div>

          <a
            href="#"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#1877F2] text-sm font-medium text-white transition-colors hover:bg-[#1877F2]/90"
          >
            <FacebookIcon className="size-4" aria-hidden />
            <span>ลงทะเบียนด้วย Facebook</span>
          </a>

          <p className="mt-6 text-center text-sm text-text-muted">
            คุณมีบัญชีอยู่แล้ว?{' '}
            <Link to="/login" className="font-medium text-accent hover:underline">
              เข้าสู่ระบบ
            </Link>
          </p>
          </>
          )}
        </motion.div>
      </div>
    </div>
  )
}
